import React, { useState, DragEvent, useRef, useEffect, useImperativeHandle, forwardRef } from 'react';
import { Modal, Input, Button, notification, Tooltip, Select, Checkbox } from 'antd';
import {
  MessageOutlined,
  DeleteOutlined,
  InboxOutlined,
  EyeOutlined,
  ReloadOutlined,
} from '@ant-design/icons';
import type { RcFile } from 'antd/es/upload/interface';
import styled from 'styled-components';
import openNotification from 'components/utils/CustomNotification';

const DEFAULT_POSITION = { x: window.innerWidth - 100, y: window.innerHeight - 100 };

const DraggableFloater = styled.div<{ x: number; y: number }>`
  position: fixed;
  left: ${({ x }) => x}px;
  top: ${({ y }) => y}px;
  background: #1890ff;
  color: white;
  padding: 1rem;
  border-radius: 50%;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.2);
  cursor: grab;
  z-index: 1000;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background 0.3s;

  &:hover .reset-button {
    display: flex;
  }
`;

const ResetButton = styled.div`
  position: absolute;
  top: -10px;
  right: -10px;
  background: #fff;
  color: #1890ff;
  border: 1px solid #ccc;
  border-radius: 50%;
  width: 20px;
  height: 20px;
  font-size: 12px;
  display: none;
  align-items: center;
  justify-content: center;
  cursor: pointer;
`;

const DropArea = styled.div`
  border: 2px dashed #d9d9d9;
  border-radius: 8px;
  padding: 30px;
  text-align: center;
  cursor: pointer;
  color: #999;
  margin-bottom: 16px;
  transition: border-color 0.3s;

  &:hover {
    border-color: #1890ff;
  }
`;

const UploadPreview = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  margin-bottom: 16px;
`;

const ImageContainer = styled.div`
  position: relative;
  width: 100px;
  height: 100px;
  border-radius: 6px;
  overflow: hidden;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
  border: 1px solid #f0f0f0;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }

  .actions {
    position: absolute;
    top: 4px;
    right: 4px;
    display: flex;
    flex-direction: column;
    gap: 4px;
  }

  button {
    background: rgba(0, 0, 0, 0.7);
    color: white;
    border: none;
    cursor: pointer;
    border-radius: 50%;
    width: 24px;
    height: 24px;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 0;
    transition: background 0.2s;

    &:hover {
      background: rgba(0, 0, 0, 0.9);
    }
  }
`;

const MAX_IMAGES = 3;
const MAX_FILE_SIZE_MB = 2;
export type FeedbackFloaterRef = {
  openWithData: (feedbackData: any) => void;
};
type FeedbackFloaterProps = {
  onFeedbackUpdated?: () => void; // parent callback
};

const FeedbackFloater = forwardRef<FeedbackFloaterRef, FeedbackFloaterProps>((props, ref) => {
  const { onFeedbackUpdated } = props;
  const [open, setOpen] = useState(false);
  const [description, setDescription] = useState('');
  const [imageFiles, setImageFiles] = useState<File[]>([]);
  const [previewVisible, setPreviewVisible] = useState(false);
  const [previewImage, setPreviewImage] = useState('');
  const [floaterPos, setFloaterPos] = useState(DEFAULT_POSITION);
  const [showReset, setShowReset] = useState(false);
  const dragging = useRef(false);
  const offset = useRef({ x: 0, y: 0 });
  const wasDragged = useRef(false);
  const [feedbackTypes, setFeedbackTypes] = useState<string[]>([]);
  const [componentTypes, setComponentTypes] = useState<string[]>([]);
  const [selectedFeedbackType, setSelectedFeedbackType] = useState<string | null>(null);
  const [selectedComponentType, setSelectedComponentType] = useState<string | null>(null);
  const [componentTypeMap, setComponentTypeMap] = useState<Record<string, string[]>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [previewUrls, setPreviewUrls] = useState<string[]>([]);
  const [editingFeedbackUid, setEditingFeedbackUid] = useState<string | null>(null);
  const [editingFeedbackData, setEditingFeedbackData] = useState<any | null>(null);

  const [severityList, setSeverityList] = useState<string[]>([]);
  const [priorityList, setPriorityList] = useState<string[]>([]);
  const [statusList, setStatusList] = useState<string[]>([]);

  const [selectedSeverity, setSelectedSeverity] = useState<string | null>(null);
  const [selectedPriority, setSelectedPriority] = useState<string | null>(null);
  const [selectedStatus, setSelectedStatus] = useState<string | null>(null);
  const [emailNotification, setEmailNotification] = useState<boolean>(false);
  const [actionMessage, setActionMessage] = useState<string>('');


  useImperativeHandle(ref, () => ({
    openWithData(feedbackData) {
      if (!feedbackData) return;

      setOpen(true);
      setDescription(feedbackData.description || '');
      setSelectedFeedbackType(feedbackData.feedback_type || null);
      setSelectedComponentType(feedbackData.component_type || null);
      setPreviewUrls(feedbackData.images || []);
      setEditingFeedbackUid(feedbackData.id || null);
      setEditingFeedbackData(feedbackData);
      setSelectedSeverity(feedbackData.severity || null);
      setSelectedPriority(feedbackData.priority || null);
      setSelectedStatus(feedbackData.feedback_status || null);
      setActionMessage(feedbackData.action_message || '');
    },
  }));

  useEffect(() => {
    if (!open) return;

    // Only fetch filter data for editing
    if (editingFeedbackUid) {
      fetch(`${process.env.REACT_APP_API_URL}/api/feedback-filter?filter_type=get`, {
        headers: {
          "user-uid": localStorage.getItem("user_uid") || "",
        }, credentials: "include",
      })
        .then(res => res.json())
        .then(resData => {
          if (resData.status === "Success") {
            const filterMap = resData.data.filter_map || {};

            setFeedbackTypes(Object.keys(filterMap));
            setComponentTypeMap(filterMap);
            setComponentTypes((Object.values(filterMap) as string[][]).flat());
            setSeverityList(resData.data.severity_list || []);
            setPriorityList(resData.data.priority_list || []);
            setStatusList(resData.data.status_list || []);
          }
        })
        .catch(err => console.error(err));
    } else {
      // For adding feedback, fetch only feedback types (or set default if static)
      fetch(`${process.env.REACT_APP_API_URL}/api/feedback-filter?filter_type=add`, {
        headers: {
          "user-uid": localStorage.getItem("user_uid") || "",
        }, credentials: "include",
      })
        .then((res) => res.json())
        .then((data) => {
          const feedbackTypes = Object.keys(data.data);
          setFeedbackTypes(feedbackTypes);
          setComponentTypeMap(data.data);

          const allComponentTypes = Object.values(data.data).flat();
          setComponentTypes(allComponentTypes as string[]);
        });
    }
  }, [open, editingFeedbackUid]);




  const handleOpen = () => {
    if (!wasDragged.current) setOpen(true);
    wasDragged.current = false;
  };

  useEffect(() => {
    // Cleanup function when component unmounts
    return () => {
      previewUrls.forEach(url => URL.revokeObjectURL(url));
      if (previewImage) {
        URL.revokeObjectURL(previewImage);
      }
    };
  }, [previewUrls, previewImage]);

  const handleClose = () => {
    setOpen(false);
    setDescription('');

    // Clean up existing preview URLs before clearing imageFiles
    previewUrls.forEach(url => URL.revokeObjectURL(url));
    setPreviewUrls([]);

    setImageFiles([]);
    setPreviewVisible(false);

    // Clean up preview image URL if it exists
    if (previewImage) {
      URL.revokeObjectURL(previewImage);
      setPreviewImage('');
    }

    // Reset selected values
    setSelectedFeedbackType(null);
    setSelectedComponentType(null);
    setEmailNotification(false);
    setActionMessage('');
    setEditingFeedbackUid(null);
    setEditingFeedbackData(null);
  };

  const handleMouseDown = (e: React.MouseEvent) => {
    dragging.current = true;
    offset.current = {
      x: e.clientX - floaterPos.x,
      y: e.clientY - floaterPos.y,
    };
  };

  const handleMouseMove = (e: MouseEvent) => {
    if (dragging.current) {
      wasDragged.current = true;
      setShowReset(true);
      setFloaterPos({ x: e.clientX - offset.current.x, y: e.clientY - offset.current.y });
    }
  };

  const isDuplicateFile = (newFile: File, existingFiles: File[]): boolean => {
    return existingFiles.some(existingFile =>
      existingFile.name === newFile.name &&
      existingFile.size === newFile.size &&
      existingFile.type === newFile.type &&
      existingFile.lastModified === newFile.lastModified
    );
  };

  const handleMouseUp = () => {
    dragging.current = false;
  };

  const handleResetPosition = (e: React.MouseEvent) => {
    e.stopPropagation();
    setFloaterPos(DEFAULT_POSITION);
    setShowReset(false);
  };

  useEffect(() => {
    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);
    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };
  }, []);

  const handleSubmit = async () => {
    if (isSubmitting) return;

    if (!selectedFeedbackType || !selectedComponentType || !description.trim()) {
      openNotification({ type: "error", message: "Please fill all required fields." });
      return;
    }

    setIsSubmitting(true);

    const uidToEdit = editingFeedbackUid; // capture current value

    const convertToBase64 = (file: File): Promise<string> =>
      new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve((reader.result as string).split(',')[1]);
        reader.onerror = reject;
        reader.readAsDataURL(file);
      });

    try {
      const base64Images = await Promise.all(imageFiles.map(convertToBase64));

      //console.log("editingFeedbackUid at submit:", editingFeedbackUid);
      if (uidToEdit && typeof uidToEdit === 'string' && uidToEdit.trim() !== '') {
        // PUT request
        const payload = {
          feedback_uid: uidToEdit,
          feedback_status: selectedStatus,
          component_type: selectedComponentType,
          severity: selectedSeverity,
          priority: selectedPriority,
          action_message: actionMessage || editingFeedbackData?.action_mesage,
          org_uid: editingFeedbackData?.org_uid
        };

        const res = await fetch(`${process.env.REACT_APP_API_URL}/api/update-feedback`, {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            "user-uid": localStorage.getItem("user_uid") || "",
          }, credentials: "include",
          body: JSON.stringify(payload),
        });

        const result = await res.json();
        if (result.status === "Success") {
          openNotification({ type: "success", message: "Feedback updated successfully" });
          handleClose();
          onFeedbackUpdated?.();
        } else {
          openNotification({ type: "error", message: result.data || "Failed to update feedback" });
        }
        if (onFeedbackUpdated) onFeedbackUpdated();
      } else {
        // POST request
        const payload = {
          feedback_type: selectedFeedbackType,
          component_type: selectedComponentType,
          description,
          image_list: base64Images,
          email_notification: emailNotification,
        };

        const res = await fetch(`${process.env.REACT_APP_API_URL}/api/feedback`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "user-uid": localStorage.getItem("user_uid") || "",
          }, credentials: "include",
          body: JSON.stringify(payload),
        });

        const result = await res.json();
        if (result.status === "Success") {
          openNotification({ type: "success", message: "Feedback submitted successfully" });
          handleClose();
        } else {
          openNotification({ type: "error", message: result.data || "Failed to submit feedback" });
        }
      }
    } catch (err) {
      console.error(err);
      openNotification({ type: "error", message: "Something went wrong. Try again later." });
    } finally {
      setIsSubmitting(false);

    }
  };



  const handleFiles = (files: FileList | File[]) => {
    const validFiles: File[] = [];
    const newPreviewUrls: string[] = [];

    for (let file of Array.from(files)) {
      // Improved duplicate check with better error message
      if (isDuplicateFile(file, imageFiles)) {
        notification.warning({
          message: "Duplicate Image",
          description: `"${file.name}" is already selected. Please choose a different image.`,
          duration: 3
        });
        continue;
      }

      // Check file type
      if (!file.type.startsWith('image/')) {
        notification.error({
          message: "Invalid File Type",
          description: `"${file.name}" is not a valid image file.`
        });
        continue;
      }

      if (file.size > MAX_FILE_SIZE_MB * 1024 * 1024) {
        notification.error({
          message: "File Too Large",
          description: `"${file.name}" is larger than ${MAX_FILE_SIZE_MB}MB`
        });
        continue;
      }

      if (imageFiles.length + validFiles.length >= MAX_IMAGES) {
        notification.warning({
          message: "Maximum Images Reached",
          description: `Maximum ${MAX_IMAGES} images allowed.`
        });
        break;
      }

      validFiles.push(file);
      // Create preview URL for each valid file
      newPreviewUrls.push(URL.createObjectURL(file));
    }

    if (validFiles.length > 0) {
      setImageFiles(prev => [...prev, ...validFiles]);
      setPreviewUrls(prev => [...prev, ...newPreviewUrls]);
    }
  };

  const handleDrop = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();

    const files = Array.from(e.dataTransfer.files);
    const duplicates = files.filter(file => isDuplicateFile(file, imageFiles));

    if (duplicates.length > 0) {
      notification.warning({
        message: "Duplicate Images Detected",
        description: `${duplicates.length} duplicate image(s) were skipped.`,
        duration: 4
      });
    }

    handleFiles(e.dataTransfer.files);
  };

  const handleRemoveImage = (index: number) => {
    // Clean up the URL for the removed image
    const urlToRevoke = previewUrls[index];
    if (urlToRevoke) {
      URL.revokeObjectURL(urlToRevoke);
    }

    setImageFiles(prev => prev.filter((_, i) => i !== index));
    setPreviewUrls(prev => prev.filter((_, i) => i !== index));
  };

  const handlePreviewImage = (index: number) => {
    // Use the existing preview URL instead of creating a new one
    const existingUrl = previewUrls[index];
    if (existingUrl) {
      setPreviewImage(existingUrl);
      setPreviewVisible(true);
    }
  };

  const handlePreviewClose = () => {
    setPreviewVisible(false);
    // Don't revoke the URL here since it's still being used in the preview list
  };

  const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files) {
      handleFiles(files);
    }
    // Clear the input value to allow selecting the same file again after removal
    e.target.value = '';
  };

  return (
    <>
      <DraggableFloater
        x={floaterPos.x}
        y={floaterPos.y}
        onMouseDown={handleMouseDown}
        onClick={handleOpen}
      >
        <MessageOutlined style={{ fontSize: 24 }} />
        {showReset && (
          <Tooltip title="Reset position">
            <ResetButton className="reset-button" onClick={handleResetPosition}>
              <ReloadOutlined />
            </ResetButton>
          </Tooltip>
        )}
      </DraggableFloater>

      <Modal
        title="Submit Feedback"
        open={open}
        onCancel={handleClose}
        footer={null}
        destroyOnClose={false}
      >
        {editingFeedbackUid && <div style={{ marginBottom: 4 }}>Feedback Type</div>}
        <Select
          placeholder="Select Feedback Type*"
          value={selectedFeedbackType}
          onChange={setSelectedFeedbackType}
          options={feedbackTypes.map(type => ({ value: type, label: type }))}
          style={{ width: '100%', marginBottom: 16 }}
          disabled={!!editingFeedbackUid}
        />

        {editingFeedbackUid && <div style={{ marginBottom: 4 }}>Component Type</div>}
        <Select
          placeholder="Select Component Type*"
          value={selectedComponentType}
          onChange={setSelectedComponentType}
          options={
            selectedFeedbackType
              ? (componentTypeMap[selectedFeedbackType] || []).map(type => ({ value: type, label: type }))
              : []
          }
          style={{ width: '100%', marginBottom: 16 }}
          disabled={!selectedFeedbackType || !!editingFeedbackUid}
        />

        {editingFeedbackUid && <div style={{ marginBottom: 4 }}>Describe the issue or feedback</div>}
        <Input.TextArea
          placeholder="Describe the issue or feedback*"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          rows={4}
          style={{ marginBottom: 16 }}
          disabled={!!editingFeedbackUid}
        />

        {editingFeedbackUid && (
          <>
            <div style={{ marginBottom: 4 }}>Severity</div>
            <Select
              placeholder="Select Severity*"
              value={selectedSeverity}
              onChange={setSelectedSeverity}
              options={severityList.map(s => ({ value: s, label: s }))}
              style={{ width: '100%', marginBottom: 16 }}
            />

            <div style={{ marginBottom: 4 }}>Priority</div>
            <Select
              placeholder="Select Priority*"
              value={selectedPriority}
              onChange={setSelectedPriority}
              options={priorityList.map(p => ({ value: p, label: p }))}
              style={{ width: '100%', marginBottom: 16 }}
            />

            <div style={{ marginBottom: 4 }}>Status</div>
            <Select
              placeholder="Select Status*"
              value={selectedStatus}
              onChange={setSelectedStatus}
              options={statusList.map(s => ({ value: s, label: s }))}
              style={{ width: '100%', marginBottom: 16 }}
            />

            <div style={{ marginBottom: 4 }}>Action Message</div>
            <Input.TextArea
              placeholder="Enter action message (optional)"
              value={actionMessage}
              onChange={(e) => setActionMessage(e.target.value)}
              rows={2}
              style={{ marginBottom: 16 }}
            />
          </>
        )}

        {!editingFeedbackUid && (
          <div style={{ marginBottom: 16 , color:"var(--text-color"}}>
            <Checkbox
              checked={emailNotification}
              onChange={(e) => setEmailNotification(e.target.checked)}
            ><span style={{color:"var(--text-color"}}>
              Receive email updates on this feedback</span>
            </Checkbox>
          </div>
        )}

        <div style={{ marginBottom: 8, fontSize: '12px', color: 'var(--text-color)' }}>
          Upload images by clicking or dragging (Max {MAX_IMAGES}, {MAX_FILE_SIZE_MB}MB each*)
        </div>

        <DropArea
          onDrop={handleDrop}
          onDragOver={(e) => e.preventDefault()}
          onClick={
            !editingFeedbackUid
              ? () => document.getElementById('fileInput')?.click()
              : undefined
          }
          style={{
            opacity: editingFeedbackUid ? 0.5 : 1,
            cursor: editingFeedbackUid ? 'not-allowed' : 'pointer',
          }}
        >
          <div style={{ cursor: editingFeedbackUid ? 'not-allowed' : 'pointer' }}>
            <InboxOutlined style={{ fontSize: 36, marginBottom: 8 }} />
            <div>Click or drag files here to upload</div>
          </div>
          <input
            id="fileInput"
            type="file"
            accept="image/*"
            multiple
            onChange={handleFileInputChange}
            style={{ display: 'none' }}
            disabled={!!editingFeedbackUid}
          />
        </DropArea>

        <UploadPreview>
          {imageFiles.map((file, index) => (
            <ImageContainer key={`${file.name}-${index}`}>
              <img
                src={previewUrls[index]}
                alt={`preview-${index}`}
                onError={(e) => {
                  console.error('Failed to load image preview:', file.name);
                  e.currentTarget.style.display = 'none';
                }}
              />
              <div className="actions">
                <button onClick={() => handlePreviewImage(index)}>
                  <EyeOutlined style={{ fontSize: 12 }} />
                </button>
                <button onClick={() => handleRemoveImage(index)}>
                  <DeleteOutlined style={{ fontSize: 12 }} />
                </button>
              </div>
            </ImageContainer>
          ))}
        </UploadPreview>

        <Button
          type="primary"
          onClick={handleSubmit}
          block
          disabled={isSubmitting}
          loading={isSubmitting}
        >
          Submit Feedback
        </Button>
      </Modal>

      <Modal
        open={previewVisible}
        footer={null}
        onCancel={handlePreviewClose}
        centered
        width="auto"
        style={{ maxWidth: '90vw' }}
      >
        <img
          src={previewImage}
          alt="Preview"
          style={{
            width: '100%',
            maxHeight: '80vh',
            objectFit: 'contain'
          }}
          onError={() => {
            notification.error({ message: "Failed to load preview image" });
            setPreviewVisible(false);
          }}
        />
      </Modal>
    </>
  );
});

export default FeedbackFloater;